import { useReduced } from '@theme/maths'

const ITEM_SELECTOR = '[data-typo-scroll-item]'
const LIST_SELECTOR = '[data-typo-scroll-list]'
const CLONE_CLASS = 'typo-scroll__list--clone'

function prefersCoarsePointer() {
  return window.matchMedia('(hover: none) and (pointer: coarse)').matches
}

function distanceToViewportCenter(item) {
  const rect = item.getBoundingClientRect()
  if (rect.bottom < 0 || rect.top > window.innerHeight)
    return Number.POSITIVE_INFINITY
  return Math.abs(window.innerHeight / 2 - (rect.top + rect.height / 2))
}

function clonePreview(element) {
  const clone = element.cloneNode(true)
  clone.setAttribute('aria-hidden', 'true')
  for (const link of clone.querySelectorAll('a')) link.tabIndex = -1
  return clone
}

class TypoScroll extends HTMLElement {
  #items = []
  #sources = []
  #list = null
  #clone = null
  #frame = 0
  #resizeFrame = 0

  #rebuild = () => {
    this.#removeHoverListeners()
    this.#clone?.remove()
    this.#list.replaceChildren(...this.#sources)
    if (this.dataset.typoScrollInfinite === 'true' && !useReduced()) {
      this.#fillViewport()
      this.#clone = clonePreview(this.#list)
      this.#clone.classList.add(CLONE_CLASS)
      this.#list.after(this.#clone)
    }
    this.#items = [...this.querySelectorAll(ITEM_SELECTOR)]
    this.#addHoverListeners()
    this.#setActive(null)
    cancelAnimationFrame(this.#frame)
    this.#frame = requestAnimationFrame(this.#trackNearestItem)
    window.lenis?.resize()
  }

  #queueResize = () => {
    if (!this.isConnected) return
    cancelAnimationFrame(this.#resizeFrame)
    this.#resizeFrame = requestAnimationFrame(this.#rebuild)
  }

  #activateHovered = (event) => {
    if (!prefersCoarsePointer()) this.#setActive(event.currentTarget)
  }

  #activateFocused = (event) => {
    const item = event.target.closest(ITEM_SELECTOR)
    if (item) this.#setActive(item)
  }

  #clearActive = () => {
    if (!this.contains(document.activeElement)) this.#setActive(null)
  }

  #clearFocus = (event) => {
    if (!this.contains(event.relatedTarget)) this.#setActive(null)
  }

  #trackNearestItem = () => {
    if (!prefersCoarsePointer()) return
    this.#frame = requestAnimationFrame(this.#trackNearestItem)
    const bounds = this.getBoundingClientRect()
    const center = window.innerHeight / 2
    if (center < bounds.top || center > bounds.bottom) {
      this.#setActive(null)
      return
    }
    const nearest = this.#items.reduce((closest, item) => {
      if (!closest) return item
      return distanceToViewportCenter(item) < distanceToViewportCenter(closest)
        ? item
        : closest
    }, null)
    this.#setActive(nearest)
  }

  connectedCallback() {
    this.#list = this.querySelector(LIST_SELECTOR)
    if (!this.#list) return
    this.#sources = [...this.#list.children]
    this.#rebuild()
    this.addEventListener('mouseleave', this.#clearActive)
    this.addEventListener('focusin', this.#activateFocused)
    this.addEventListener('focusout', this.#clearFocus)
    window.addEventListener('resize', this.#queueResize)
    document.fonts.ready.then(this.#queueResize)
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.#frame)
    cancelAnimationFrame(this.#resizeFrame)
    this.removeEventListener('mouseleave', this.#clearActive)
    this.removeEventListener('focusin', this.#activateFocused)
    this.removeEventListener('focusout', this.#clearFocus)
    window.removeEventListener('resize', this.#queueResize)
    this.#clone?.remove()
    this.#list?.replaceChildren(...this.#sources)
  }

  #fillViewport() {
    const height = this.#list.getBoundingClientRect().height
    if (!height) return
    const copies = Math.ceil(window.innerHeight / height)
    for (let copy = 1; copy < copies; copy++) {
      this.#list.append(...this.#sources.map(clonePreview))
    }
  }

  #addHoverListeners() {
    for (const item of this.#items) {
      item.addEventListener('mouseenter', this.#activateHovered)
    }
  }

  #removeHoverListeners() {
    for (const item of this.#items) {
      item.removeEventListener('mouseenter', this.#activateHovered)
    }
  }

  #setActive(target) {
    for (const item of this.#items) {
      const state = item === target ? 'active' : ''
      if (item.getAttribute('data-typo-scroll-item') !== state) {
        item.setAttribute('data-typo-scroll-item', state)
      }
    }
  }
}

if (!customElements.get('typo-scroll')) {
  customElements.define('typo-scroll', TypoScroll)
}
